import React, { useState, useEffect } from 'react';
import axios from '../../axiosInstance';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';

function MentoringAgreementForm({
  user,
  mentor,
  menteeName,
  mentorName,
  goals = [],
  competences = [],
  whyMentoring = '',
  onClose
}) {
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [frequency, setFrequency] = useState('');
  const [contactAllowed, setContactAllowed] = useState(false);
  const [locationType, setLocationType] = useState('');
  const [customLocation, setCustomLocation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (startDate) {
      const start = new Date(startDate);
      const end = new Date(start);
      end.setFullYear(end.getFullYear() + 1);
      setEndDate(end.toISOString().split('T')[0]);
    }
  }, [startDate]);

  const frequencyLabel = (v) => {
    if (v === 'wöchentlich') return t('mentoringAgreementForm.frequency.weekly', { defaultValue: 'Weekly' });
    if (v === '14-tägig') return t('mentoringAgreementForm.frequency.biweekly', { defaultValue: 'Every two weeks' });
    if (v === 'monatlich') return t('mentoringAgreementForm.frequency.monthly', { defaultValue: 'Monthly' });
    return '';
  };

  const locationLabel = (v) => {
    if (v === 'online') return t('mentoringAgreementForm.location.online', { defaultValue: 'Online' });
    if (v === 'telefonisch') return t('mentoringAgreementForm.location.phone', { defaultValue: 'By phone' });
    if (v === 'persönlich') return t('mentoringAgreementForm.location.inPerson', { defaultValue: 'In person' });
    return '';
  };

  const handleSubmit = async () => {
    if (!confirmation) return;

    setIsSubmitting(true);
    try {
      const period = `${startDate} ${t('mentoringAgreementForm.periodTo', { defaultValue: 'to' })} ${endDate}`;

      const turnus = frequencyLabel(frequency);
      const contactOption = contactAllowed
        ? t('common.yes', { defaultValue: 'Yes' })
        : t('common.no', { defaultValue: 'No' });

      const location =
        locationType === 'persönlich'
          ? (customLocation || '')
          : locationLabel(locationType);

      // Log alle gesendeten Werte vor dem POST-Request
      console.log('[MentoringAgreementForm] Sende an Backend:', {
        mentee_user_id: user?.user_id,
        mentor_user_id: mentor?.id,
        mentee_email: user?.email,
        mentor_email: mentor?.email || '',
        period,
        turnus,
        contact_option: contactOption,
        location,
        goals,
        topics: competences,
        whyMentoring,
        mentee_confirmed: true
      });

      const response = await axios.post(
        '/mentoring/agreement',
        {
          mentee_user_id: user?.user_id,
          mentor_user_id: mentor?.id,
          mentee_email: user?.email,
          mentor_email: mentor?.email || '',
          period,
          turnus,
          contact_option: contactOption,
          location,
          goals,
          topics: competences,
          whyMentoring,
          mentee_confirmed: true
        },
        { withCredentials: true }
      );

      // Log Server-Response
      console.log('[MentoringAgreementForm] Antwort vom Backend:', response.data);
      setSubmitted(true);
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      alert(
        t('mentoringAgreementForm.saveError', {
          defaultValue: 'Error saving the agreement. Please try again later.'
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <p className="text-green-600">
        {t('mentoringAgreementForm.savedMessage', { defaultValue: 'Agreement has been saved.' })}
      </p>
    );
  }

  return (
    <div className="bg-white text-black p-6 rounded-xl space-y-6 max-w-3xl mx-auto">
      <h2 className="text-xl font-bold">
        {t('mentoringAgreementView.heading', { defaultValue: 'Mentoring Agreement' })}
      </h2>

      <p>
        {t('mentoringAgreementForm.mentorLabel', { defaultValue: 'Mentor' })}:{' '}
        <strong>{mentorName || '---'}</strong>
      </p>

      <p>
        {t('mentoringAgreementForm.menteeLabel', { defaultValue: 'Mentee' })}:{' '}
        <strong>{menteeName && menteeName !== 'undefined undefined' ? menteeName : '---'}</strong>
      </p>

      <label className="block mt-4 mb-1">
        {t('mentoringAgreementForm.startDateLabel', { defaultValue: 'Start date' })}:
      </label>
      <input
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        className="border p-2 w-full"
      />

      <label className="block mt-4 mb-1">
        {t('mentoringAgreementForm.endDateLabel', { defaultValue: 'End date (automatic)' })}:
      </label>
      <input
        type="date"
        value={endDate}
        disabled
        className="border p-2 w-full bg-gray-100"
      />

      <label className="block mt-4 mb-1">
        {t('mentoringAgreementForm.frequencyLabel', { defaultValue: 'Frequency' })}:
      </label>
      <select
        value={frequency}
        onChange={(e) => setFrequency(e.target.value)}
        className="border p-2 w-full"
      >
        <option value="">
          {t('common.pleaseSelect', { defaultValue: 'Please select' })}
        </option>
        <option value="wöchentlich">
          {t('mentoringAgreementForm.frequency.weekly', { defaultValue: 'Weekly' })}
        </option>
        <option value="14-tägig">
          {t('mentoringAgreementForm.frequency.biweekly', { defaultValue: 'Every two weeks' })}
        </option>
        <option value="monatlich">
          {t('mentoringAgreementForm.frequency.monthly', { defaultValue: 'Monthly' })}
        </option>
      </select>

      <label className="block mt-4 mb-1">
        {t('mentoringAgreementForm.contactAllowedLabel', { defaultValue: 'Additional contact allowed?' })}
      </label>
      <select
        value={contactAllowed ? 'true' : 'false'}
        onChange={(e) => setContactAllowed(e.target.value === 'true')}
        className="border p-2 w-full"
      >
        <option value="false">{t('common.no', { defaultValue: 'No' })}</option>
        <option value="true">{t('common.yes', { defaultValue: 'Yes' })}</option>
      </select>

      <label className="block mt-4 mb-1">
        {t('mentoringAgreementForm.locationLabel', { defaultValue: 'Meeting location' })}:
      </label>
      <select
        value={locationType}
        onChange={(e) => setLocationType(e.target.value)}
        className="border p-2 w-full"
      >
        <option value="">
          {t('common.pleaseSelect', { defaultValue: 'Please select' })}
        </option>
        <option value="online">{t('mentoringAgreementForm.location.online', { defaultValue: 'Online' })}</option>
        <option value="telefonisch">{t('mentoringAgreementForm.location.phone', { defaultValue: 'By phone' })}</option>
        <option value="persönlich">{t('mentoringAgreementForm.location.inPerson', { defaultValue: 'In person' })}</option>
      </select>

      {locationType === 'persönlich' && (
        <input
          type="text"
          placeholder={t('mentoringAgreementForm.customLocationPlaceholder', { defaultValue: 'Enter location' })}
          value={customLocation}
          onChange={(e) => setCustomLocation(e.target.value)}
          className="border p-2 mt-2 w-full"
        />
      )}

      <div className="border-t pt-4 space-y-4">
        <section>
          <p className="mb-2">{t('mentoringAgreementView.goalsTitle', { defaultValue: 'Goals of the mentoring sessions' })}:</p>
          <ul className="list-disc list-inside">
            {goals.map((goal, index) => <li key={index}>{goal}</li>)}
          </ul>
        </section>

        <section>
          <p className="mb-2">{t('mentoringAgreementView.topicsTitle', { defaultValue: 'Topics to discuss' })}:</p>
          <ul className="list-disc list-inside">
            {competences.map((topic, index) => <li key={index}>{topic}</li>)}
          </ul>
        </section>

        <section>
          <p className="mb-2">{t('mentoringAgreementView.motivationTitle', { defaultValue: 'Motivation for participation' })}:</p>
          <p className="text-gray-800">{whyMentoring}</p>
        </section>

        <section>
          <p>{t('mentoringAgreementView.intro')}</p>
          <p>{t('mentoringAgreementView.principlesIntro')}</p>
          <ul className="list-disc list-inside">
            {(t('mentoringAgreementView.principles', { returnObjects: true }) || []).map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </section>
      </div>

      <div className="mt-4">
        <label>
          <input
            type="checkbox"
            checked={confirmation}
            onChange={(e) => setConfirmation(e.target.checked)}
            className="mr-2"
          />
          {t('mentoringAgreementForm.confirmationText', {
            defaultValue: 'I confirm the details of this mentoring agreement.'
          })}
        </label>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!confirmation || isSubmitting}
        className="bg-mentixxgreen text-black mt-4"
      >
        {t('mentoringAgreementForm.saveButton', { defaultValue: 'Save agreement' })}
      </Button>
    </div>
  );
}

export default MentoringAgreementForm;
